import { Component } from '@theme/component';

/**
 * SOL language picker. All navigation submits Shopify's original localization form;
 * its return path, CSRF field, country and native language select stay authoritative.
 * @typedef {object} SolLanguageRefs
 * @property {HTMLButtonElement} trigger
 * @property {HTMLDivElement} panel
 * @property {HTMLButtonElement[]} choices
 * @extends {Component<SolLanguageRefs>}
 */
class SolLanguageSelector extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.setup();
  }

  updatedCallback() {
    super.updatedCallback();
    this.setup();
  }

  setup() {
    this.controller?.abort();
    this.close();
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    this.nativeSelect = this.parentElement?.querySelector('select[name="language_code"]');
    this.fallback = this.parentElement?.querySelector('[data-sol-language-fallback]');
    if (!this.nativeSelect || !this.refs.trigger || !this.refs.panel || !this.refs.choices?.length) return;
    this.nativeSelect.hidden = true;
    if (this.fallback) this.fallback.hidden = true;
    this.hidden = false;
    this.typeBuffer = '';
    this.addEventListener('click', event => {
      const choice = event.target.closest('[data-language-value]');
      if (choice && this.contains(choice)) {
        const value = choice.dataset.languageValue;
        this.close(true);
        if (!value || value === this.nativeSelect.value) return;
        this.nativeSelect.value = value;
        const form = this.nativeSelect.form;
        if (form?.requestSubmit) form.requestSubmit();
        else form?.submit();
      } else if (this.refs.trigger.contains(event.target)) {
        if (this.open) this.close(true);
        else this.show();
      }
    }, options);
    this.addEventListener('keydown', this.onKeyDown, options);
    // The theme drawer handles Escape on keyup. Consume that same key release
    // after closing this menu so one Escape closes only the innermost layer.
    this.addEventListener('keyup', event => {
      if (event.key === 'Escape' && this.consumeEscapeKeyUp) {
        event.preventDefault();
        event.stopPropagation();
        this.consumeEscapeKeyUp = false;
      }
    }, options);
    this.addEventListener('focusout', event => {
      if (this.open && event.relatedTarget && !this.contains(event.relatedTarget)) this.close();
    }, options);
    document.addEventListener('pointerdown', event => {
      if (this.open && !this.contains(event.target)) this.close();
    }, { capture: true, ...options });
    // Avoid a detached top-layer menu when its containing mobile drawer closes.
    for (let ancestor = this.parentElement; ancestor; ancestor = ancestor.parentElement) {
      if (ancestor instanceof HTMLDetailsElement) {
        ancestor.addEventListener('toggle', () => { if (!ancestor.open) this.close(); }, options);
      }
    }
    window.addEventListener('resize', this.reposition, options);
    window.visualViewport?.addEventListener('resize', this.reposition, options);
    window.visualViewport?.addEventListener('scroll', this.reposition, options);
    document.addEventListener('scroll', this.reposition, { capture: true, passive: true, ...options });
  }

  show(position = 'selected') {
    document.querySelectorAll('sol-language-selector').forEach(selector => { if (selector !== this) selector.close?.(); });
    this.open = true;
    this.refs.panel.hidden = false;
    this.refs.panel.showPopover?.();
    this.refs.trigger.setAttribute('aria-expanded', 'true');
    this.reposition();
    const choices = this.refs.choices;
    const selected = choices.findIndex(choice => choice.getAttribute('aria-checked') === 'true');
    const index = position === 'first' ? 0 : position === 'last' ? choices.length - 1 : Math.max(0, selected);
    this.focusChoice(index);
  }

  close(restoreFocus = false) {
    this.open = false;
    clearTimeout(this.typeTimer);
    this.typeBuffer = '';
    if (this.refs.panel) {
      if (this.refs.panel.hidePopover && this.refs.panel.matches(':popover-open')) this.refs.panel.hidePopover();
      this.refs.panel.hidden = true;
    }
    this.refs.trigger?.setAttribute('aria-expanded', 'false');
    if (restoreFocus) this.refs.trigger?.focus({ preventScroll: true });
  }

  focusChoice(index) {
    this.refs.choices.forEach((choice, itemIndex) => { choice.tabIndex = index === itemIndex ? 0 : -1; });
    this.refs.choices[index]?.focus({ preventScroll: true });
    const choice = this.refs.choices[index];
    if (!choice) return;
    const item = choice.getBoundingClientRect();
    const panel = this.refs.panel.getBoundingClientRect();
    if (item.top < panel.top + 6) this.refs.panel.scrollTop -= panel.top + 6 - item.top;
    else if (item.bottom > panel.bottom - 6) this.refs.panel.scrollTop += item.bottom - panel.bottom + 6;
  }

  onKeyDown = event => {
    if (event.key === 'Escape' && this.open) {
      event.preventDefault();
      event.stopPropagation();
      this.consumeEscapeKeyUp = true;
      this.close(true);
      return;
    }
    if (event.target === this.refs.trigger && ['ArrowDown', 'ArrowUp'].includes(event.key)) {
      event.preventDefault();
      event.stopPropagation();
      this.show(event.key === 'ArrowDown' ? 'first' : 'last');
      return;
    }
    if (!this.open) return;
    if (event.key === 'Tab') {
      this.close(true);
      return;
    }
    const choices = this.refs.choices;
    const index = choices.indexOf(document.activeElement);
    if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
      event.preventDefault();
      event.stopPropagation();
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? choices.length - 1 : (index + (event.key === 'ArrowDown' ? 1 : -1) + choices.length) % choices.length;
      this.focusChoice(next);
    } else if (event.key.length === 1 && event.key !== ' ' && !event.ctrlKey && !event.metaKey && !event.altKey) {
      clearTimeout(this.typeTimer);
      const letter = event.key.toLocaleLowerCase();
      this.typeBuffer = this.typeBuffer && [...this.typeBuffer].every(value => value === letter) ? letter : this.typeBuffer + letter;
      const candidate = choices.findIndex((choice, choiceIndex) => choiceIndex > index && choice.dataset.languageSearch.toLocaleLowerCase().split(/\s+/).some(word => word.startsWith(this.typeBuffer)));
      const wrapped = choices.findIndex(choice => choice.dataset.languageSearch.toLocaleLowerCase().split(/\s+/).some(word => word.startsWith(this.typeBuffer)));
      if (candidate >= 0 || wrapped >= 0) this.focusChoice(candidate >= 0 ? candidate : wrapped);
      this.typeTimer = setTimeout(() => { this.typeBuffer = ''; }, 650);
    }
  };

  reposition = () => {
    if (!this.open) return;
    const trigger = this.refs.trigger.getBoundingClientRect();
    const panel = this.refs.panel;
    const viewport = window.visualViewport;
    const leftEdge = (viewport?.offsetLeft || 0) + 12;
    const topEdge = (viewport?.offsetTop || 0) + 12;
    const rightEdge = (viewport?.offsetLeft || 0) + (viewport?.width || innerWidth) - 12;
    const bottomEdge = (viewport?.offsetTop || 0) + (viewport?.height || innerHeight) - 12;
    if (trigger.bottom < topEdge || trigger.top > bottomEdge) {
      this.close();
      return;
    }
    const width = Math.max(1, Math.min(300, rightEdge - leftEdge));
    const left = Math.max(leftEdge, Math.min(trigger.right - width, rightEdge - width));
    const below = Math.max(0, bottomEdge - trigger.bottom - 8);
    const above = Math.max(0, trigger.top - topEdge - 8);
    const upwards = below < Math.min(panel.scrollHeight, 360) && above > below;
    const available = Math.max(0, upwards ? above : below);
    panel.style.width = `${width}px`;
    panel.style.maxHeight = `${Math.min(416, available)}px`;
    panel.style.left = `${left}px`;
    const height = panel.getBoundingClientRect().height;
    panel.style.top = `${Math.max(topEdge, Math.min(upwards ? trigger.top - height - 8 : trigger.bottom + 8, bottomEdge - height))}px`;
  };

  disconnectedCallback() {
    this.close();
    this.controller?.abort();
    clearTimeout(this.typeTimer);
    if (this.nativeSelect) this.nativeSelect.hidden = false;
    if (this.fallback) this.fallback.hidden = false;
    super.disconnectedCallback();
  }
}

if (!customElements.get('sol-language-selector')) customElements.define('sol-language-selector', SolLanguageSelector);
